// Copyright (C) 2017-2023 Smart code 203358507

const React = require('react');
const PropTypes = require('prop-types');
const classnames = require('classnames');
const useTranslate = require('stremio/common/useTranslate');
const { default: useVisibleCatalogs } = require('stremio/common/useVisibleCatalogs');
const { useNotifications, withCoreSuspender } = require('stremio/common');
const { ContinueWatchingItem, EventModal, LiveTvContinueWatchingItem, MainNavBars, MetaItem, MetaRow } = require('stremio/components');
const useBoard = require('./useBoard');
const useContinueWatchingPreview = require('./useContinueWatchingPreview');
const { default: useLiveTvContinueWatching } = require('./useLiveTvContinueWatching');
const styles = require('./styles');
const BoardHero = require('./BoardHero');
const { hideUnapprovedCatalogs, loadAllowedCatalogRange } = require('../../caroline/catalogs/catalogPolicy');

const THRESHOLD = 5;

const BoardMetaItem = (props) => {
    const releaseYear = typeof props.releaseInfo === 'string' ? props.releaseInfo.match(/\b(?:19|20)\d{2}\b/)?.[0] : null;
    const poster = props.poster && typeof props.poster === 'object' ? props.poster : {
        src: props.poster,
        shape: props.posterShape,
        overlay: releaseYear ? <span className={styles['board-year-badge']}>{releaseYear}</span> : null,
    };

    return <MetaItem {...props} poster={poster} />;
};

BoardMetaItem.propTypes = {
    poster: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    posterShape: PropTypes.string,
    releaseInfo: PropTypes.string,
};

const Board = () => {
    const t = useTranslate();
    const continueWatchingPreview = useContinueWatchingPreview();
    const liveTvContinueWatching = useLiveTvContinueWatching();
    const [board, loadBoardRows] = useBoard();
    const notifications = useNotifications();
    const liveTvCatalog = React.useMemo(() => ({
        items: (Array.isArray(liveTvContinueWatching.items) ? liveTvContinueWatching.items : [])
            .map((item) => ({ ...item, posterShape: 'landscape' })),
    }), [liveTvContinueWatching.items]);
    const allowedCatalogs = React.useMemo(() => hideUnapprovedCatalogs(board.catalogs), [board.catalogs]);
    const catalogsRef = React.useRef(board.catalogs);
    catalogsRef.current = board.catalogs;
    const loadAllowedCatalogRows = React.useCallback((range) => {
        loadAllowedCatalogRange(catalogsRef.current, range, loadBoardRows);
    }, [loadBoardRows]);
    const boardCatalogsOffset =
        (continueWatchingPreview.items.length > 0 ? 1 : 0) +
        (liveTvCatalog.items.length > 0 ? 1 : 0);
    const { catalogRows, scrollContainerRef, onScroll } = useVisibleCatalogs({
        catalogs: allowedCatalogs,
        loadRange: loadAllowedCatalogRows,
        leadingRows: boardCatalogsOffset,
        preloadRows: THRESHOLD,
    });
    const heroCatalogRow = catalogRows.find(({ catalog }) => catalog.content?.type === 'Ready' && catalog.content.content?.length > 0);
    const heroItem = heroCatalogRow?.catalog.content.content[0] ?? null;
    const featuredCatalogIndex = heroCatalogRow?.index;
    return (
        <div className={styles['board-container']}>
            <EventModal />
            <MainNavBars className={styles['board-content-container']} route={'board'}>
                <div ref={scrollContainerRef} className={styles['board-content']} onScroll={onScroll}>
                    <BoardHero item={heroItem} />
                    {
                        continueWatchingPreview.items.length > 0 ?
                            <MetaRow
                                className={classnames(styles['board-row'], styles['continue-watching-row'], 'animation-fade-in')}
                                title={t.string('BOARD_CONTINUE_WATCHING')}
                                catalog={continueWatchingPreview}
                                itemComponent={ContinueWatchingItem}
                                notifications={notifications}
                            />
                            :
                            null
                    }
                    {
                        liveTvCatalog.items.length > 0 ?
                            <MetaRow
                                className={classnames(styles['board-row'], styles['board-row-landscape'], 'animation-fade-in')}
                                title={t.string('BOARD_CONTINUE_WATCHING_LIVE')}
                                catalog={liveTvCatalog}
                                itemComponent={LiveTvContinueWatchingItem}
                                notifications={notifications}
                            />
                            :
                            null
                    }
                    {catalogRows.map(({ catalog, index }) => {
                        switch (catalog.content?.type) {
                            case 'Ready': {
                                return (
                                    <MetaRow
                                        key={index}
                                        className={classnames(styles['board-row'], styles[`board-row-${catalog.content.content[0].posterShape}`], index === featuredCatalogIndex && styles['featured-catalog-row'], 'animation-fade-in')}
                                        catalog={catalog}
                                        itemComponent={BoardMetaItem}
                                    />
                                );
                            }
                            case 'Err': {
                                if (catalog.content.content !== 'EmptyContent') {
                                    return (
                                        <MetaRow
                                            key={index}
                                            className={classnames(styles['board-row'], 'animation-fade-in')}
                                            catalog={catalog}
                                            message={t.string('GENERIC_ERROR_MESSAGE')}
                                        />
                                    );
                                }
                                return null;
                            }
                            default: {
                                return (
                                    <MetaRow.Placeholder
                                        key={index}
                                        className={classnames(styles['board-row'], styles['board-row-poster'], index === featuredCatalogIndex && styles['featured-catalog-row'], 'animation-fade-in')}
                                        catalog={catalog}
                                        title={t.catalogTitle(catalog)}
                                    />
                                );
                            }
                        }
                    })}
                </div>
            </MainNavBars>
        </div>
    );
};

const BoardFallback = () => (
    <div className={styles['board-container']}>
        <MainNavBars className={styles['board-content-container']} route={'board'} />
    </div>
);

module.exports = withCoreSuspender(Board, BoardFallback);
